import { c as _c } from "react/compiler-runtime";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { InfoIcon } from "lucide-react";
import * as React from "react";
import cn from "twsx";

// Field label: 13/500 secondary text. `muted` is for read-only key/value
// labels, `invalid` follows the field's error state. `stack` wraps a label
// above the control it names; `option` wraps a checkbox or radio and its
// 13/400 option text.
const labelVariants = cva("inline-flex gap-0.5 leading-4 peer-disabled:cursor-not-allowed peer-disabled:opacity-70", {
  variants: {
    variant: {
      default: "text-text-secondary",
      muted: "text-text-tertiary",
      invalid: "text-red-text"
    },
    size: {
      md: "text-base",
      sm: "text-sm"
    },
    layout: {
      inline: "font-medium",
      stack: "grid gap-1.5 font-medium",
      option: "flex cursor-pointer items-start gap-2 font-normal"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "md",
    layout: "inline"
  }
});

/** Use when naming a form control outside a Field. */
const Label = t0 => {
  const $ = _c(26);
  let className;
  let description;
  let isRequired;
  let layout;
  let props;
  let ref;
  let size;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      isRequired,
      variant,
      size,
      layout,
      description,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = description;
    $[3] = isRequired;
    $[4] = layout;
    $[5] = props;
    $[6] = ref;
    $[7] = size;
    $[8] = variant;
  } else {
    className = $[1];
    description = $[2];
    isRequired = $[3];
    layout = $[4];
    props = $[5];
    ref = $[6];
    size = $[7];
    variant = $[8];
  }
  let t1;
  if ($[9] !== className || $[10] !== layout || $[11] !== size || $[12] !== variant) {
    t1 = cn(labelVariants({
      variant,
      size,
      layout
    }), className);
    $[9] = className;
    $[10] = layout;
    $[11] = size;
    $[12] = variant;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  const t2 = isRequired ? "true" : "false";
  let t3;
  if ($[14] !== layout || $[15] !== props.children) {
    t3 = layout === "stack" || layout === "option" ? props.children : <span>{props.children}</span>;
    $[14] = layout;
    $[15] = props.children;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== description) {
    t4 = description && <Tooltip><TooltipTrigger tabIndex={-1}><InfoIcon className="ml-1 size-3 text-text-tertiary" /></TooltipTrigger><TooltipContent>{description}</TooltipContent></Tooltip>;
    $[17] = description;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== props || $[20] !== ref || $[21] !== t1 || $[22] !== t2 || $[23] !== t3 || $[24] !== t4) {
    t5 = <LabelPrimitive.Root ref={ref} className={t1} data-required={t2} {...props}>{t3}{t4}</LabelPrimitive.Root>;
    $[19] = props;
    $[20] = ref;
    $[21] = t1;
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  return t5;
};
Label.displayName = LabelPrimitive.Root.displayName;
export { Label };